import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  AuthSessionResponse,
  CatalogItem,
  CatalogItemSummary,
  CreateCatalogItemRequest,
  CreateReconstructionRequest,
  ReconstructionSummary,
  ReorderCatalogItemsRequest,
  UpdateCatalogItemRequest,
} from '../types';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
export const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';
export const ADMIN_TOKEN_KEY = 'catalog_admin_token';
// Session token for any logged-in user (not necessarily a platform admin),
// used for self-serve actions such as creating a reconstruction.
export const SESSION_TOKEN_KEY = 'catalog_session_token';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => config);
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => Promise.reject(error)
);

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

export function getStoredAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function storeAdminToken(token: string): void {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function getStoredSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function storeSessionToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

export async function getCatalog(): Promise<CatalogItemSummary[]> {
  const response = await apiClient.get<CatalogItemSummary[]>('/catalog');
  return response.data;
}

export async function getCatalogItem(slug: string): Promise<CatalogItem> {
  const response = await apiClient.get<CatalogItem>(`/catalog/${encodeURIComponent(slug)}`);
  return response.data;
}

export async function googleAuth(googleToken: string): Promise<AuthSessionResponse> {
  const response = await apiClient.post<AuthSessionResponse>(
    '/auth/google',
    { token: googleToken },
    { headers: authHeaders(googleToken) }
  );
  return response.data;
}

export async function devLogin(): Promise<AuthSessionResponse> {
  const response = await apiClient.post<AuthSessionResponse>('/auth/dev-login');
  return response.data;
}

export async function getAdminCatalog(token: string): Promise<CatalogItem[]> {
  const response = await apiClient.get<CatalogItem[]>('/admin/catalog', {
    headers: authHeaders(token),
  });
  return response.data;
}

export async function createCatalogItem(
  token: string,
  payload: CreateCatalogItemRequest
): Promise<CatalogItem> {
  const response = await apiClient.post<CatalogItem>('/admin/catalog', payload, {
    headers: authHeaders(token),
  });
  return response.data;
}

export async function updateCatalogItem(
  token: string,
  slug: string,
  payload: UpdateCatalogItemRequest
): Promise<CatalogItem> {
  const response = await apiClient.put<CatalogItem>(
    `/admin/catalog/${encodeURIComponent(slug)}`,
    payload,
    { headers: authHeaders(token) }
  );
  return response.data;
}

export async function reorderCatalogItems(
  token: string,
  payload: ReorderCatalogItemsRequest
): Promise<CatalogItem[]> {
  const response = await apiClient.post<CatalogItem[]>('/admin/catalog/reorder', payload, {
    headers: authHeaders(token),
  });
  return response.data;
}

export async function deleteCatalogItem(token: string, slug: string): Promise<{ success: true }> {
  const response = await apiClient.delete<{ success: true }>(
    `/admin/catalog/${encodeURIComponent(slug)}`,
    { headers: authHeaders(token) }
  );
  return response.data;
}

// ── Reconstructions ─────────────────────────────────────────────────────

export async function createReconstruction(
  token: string | null,
  payload: CreateReconstructionRequest
): Promise<ReconstructionSummary> {
  const response = await apiClient.post<ReconstructionSummary>(
    '/reconstructions',
    payload,
    token ? { headers: authHeaders(token) } : undefined
  );
  return response.data;
}

export async function listMyReconstructions(token: string): Promise<ReconstructionSummary[]> {
  const response = await apiClient.get<ReconstructionSummary[]>('/reconstructions/mine', {
    headers: authHeaders(token),
  });
  return response.data;
}
